import { useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Card, CardHeader, Input, Modal, ModalFooter, Select } from '@/components/common';
import { formatDate } from '@/utils';
import { previewInNewTab, previewOutcomeMessage } from '@/utils/documentPreview';
// Imported by path rather than through services/index.ts, which is outside
// this task's file domain.
import {
  CUSTOMER_KYC_DOCUMENT_TYPES,
  KYC_FIELD_LABELS,
  customerKycService,
  type CustomerKycDocument,
} from '@/services/kycDocuments';
import {
  useConfirmCustomerKycExtraction,
  useCustomerKycDocuments,
  useDeleteCustomerKycDocument,
  useUploadCustomerKycDocument,
  useVerifyCustomerKycDocument,
} from '@/hooks/useKycDocuments';

/**
 * A contact's KYC documents, for every side of the relationship.
 *
 * ONE component, two audiences, because they are looking at the same rows and
 * a second implementation would eventually disagree with this one about
 * whether a PAN card was accepted:
 *
 * - broker staff and the contact's POSP get `canVerify` and rule on documents;
 * - the contact themselves uploads, sees the verdict READ-ONLY, and may confirm
 *   what was read off their own card. Confirming a detail about yourself is
 *   data entry; verifying the evidence is a judgement, and the person who
 *   supplied the evidence cannot make it. The server enforces both — this prop
 *   only decides whether pointless buttons are drawn.
 *
 * The extraction block is the part worth reading twice. OCR output arrives
 * marked `unconfirmed` and NOTHING has been written to the contact at that
 * point. The panel shows what was read, lets it be corrected, and requires an
 * explicit Save — so a misread 0-for-O costs a keystroke rather than becoming a
 * false statement about a named person with nobody's signature on it.
 */

const STATUS_VARIANT: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  verified: 'success',
  rejected: 'error',
  expired: 'error',
};

/** What the extraction state means to a human, and whether it needs action. */
const EXTRACTION_NOTE: Record<string, string> = {
  not_attempted: 'No details to read from this kind of document.',
  unavailable: 'We could not read this file. Enter the details by hand.',
  no_match: 'Nothing recognisable was found in this image. Enter the details by hand.',
  discarded: 'The reading was discarded. Nothing was saved to the contact.',
};

function fieldLabel(key: string) {
  return KYC_FIELD_LABELS[key] ?? key.replace(/_/g, ' ');
}

// ── Verdict modal ─────────────────────────────────────────────────────────────

function VerdictModal({
  document,
  approve,
  onClose,
  onConfirm,
  isLoading,
}: {
  document: CustomerKycDocument | null;
  approve: boolean;
  onClose: () => void;
  onConfirm: (remarks: string) => void;
  isLoading: boolean;
}) {
  const [remarks, setRemarks] = useState('');

  const close = () => {
    setRemarks('');
    onClose();
  };

  return (
    <Modal
      isOpen={!!document}
      onClose={close}
      title={approve ? 'Verify document' : 'Reject document'}
      size="sm"
    >
      <div>
        <label htmlFor="kyc-verdict-remarks" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Remarks {approve ? '(optional)' : ''}
        </label>
        <textarea
          id="kyc-verdict-remarks"
          name="kyc-verdict-remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600"
          placeholder={
            approve ? 'e.g. Checked against the original.' : 'Say what is wrong — the customer sees this.'
          }
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          This remark is shown to the customer on their own KYC screen.
        </p>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
        <Button
          variant={approve ? 'primary' : 'danger'}
          onClick={() => onConfirm(remarks.trim())}
          // A rejection with no reason gives the customer nothing to act on,
          // and the server refuses it — so do not offer it here either.
          disabled={isLoading || (!approve && !remarks.trim())}
        >
          {isLoading ? 'Saving…' : approve ? 'Verify' : 'Reject'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Extraction review ─────────────────────────────────────────────────────────

function ExtractionReview({
  document,
  customerId,
}: {
  document: CustomerKycDocument;
  customerId: string;
}) {
  const fields = document.extracted_data?.fields ?? {};
  const fieldKeys = Object.keys(fields);
  const confirmMutation = useConfirmCustomerKycExtraction(customerId);
  // Seeded with the best candidate, EDITABLE on purpose: a reviewer who spots
  // the misread should be able to fix it here rather than abandoning the
  // reading and retyping the whole record somewhere else.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fieldKeys.map((key) => [key, fields[key].candidates[0] ?? '']))
  );

  if (fieldKeys.length === 0) return null;

  const lowConfidence = fieldKeys.some((key) => fields[key].confidence < 70);

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
      <div className="flex items-start gap-2">
        <SparklesIcon className="w-5 h-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            Read from this document — please check before saving
          </p>
          <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
            Nothing has been saved to the contact yet.
            {lowConfidence
              ? ' Some values had more than one plausible reading — pick or correct them below.'
              : ' Correct anything that is wrong.'}
          </p>

          <div className="mt-3 space-y-3">
            {fieldKeys.map((key) => {
              const field = fields[key];
              const alternatives = field.candidates.slice(1);
              return (
                <div key={key}>
                  <Input
                    id={`kyc-extract-${document.id}-${key}`}
                    name={`kyc-extract-${document.id}-${key}`}
                    label={fieldLabel(key)}
                    value={values[key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                  {alternatives.length > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span className="text-xs text-amber-700 dark:text-amber-300">Also read as:</span>
                      {alternatives.map((candidate) => (
                        <button
                          key={candidate}
                          type="button"
                          onClick={() => setValues((prev) => ({ ...prev, [key]: candidate }))}
                          className="rounded border border-amber-300 bg-white px-2 py-0.5 text-xs font-mono text-amber-900 hover:bg-amber-100 dark:bg-gray-800 dark:text-amber-200 dark:hover:bg-amber-900/30 dark:border-amber-700"
                        >
                          {candidate}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={confirmMutation.isPending || !Object.values(values).some((v) => v.trim())}
              onClick={() =>
                confirmMutation.mutate({
                  documentId: document.id,
                  fields: Object.fromEntries(
                    Object.entries(values).filter(([, value]) => value.trim())
                  ),
                })
              }
            >
              {confirmMutation.isPending ? 'Saving…' : 'Confirm and save to contact'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={confirmMutation.isPending}
              onClick={() => confirmMutation.mutate({ documentId: document.id, discard: true })}
            >
              Discard reading
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── One document row ──────────────────────────────────────────────────────────

function DocumentRow({
  document,
  customerId,
  canVerify,
  canDelete,
  onVerdict,
}: {
  document: CustomerKycDocument;
  customerId: string;
  canVerify: boolean;
  canDelete: boolean;
  onVerdict: (document: CustomerKycDocument, approve: boolean) => void;
}) {
  const deleteMutation = useDeleteCustomerKycDocument(customerId);
  const note = EXTRACTION_NOTE[document.extraction_status];

  // B13. Opening a document is now a request, not a link, so it has the three
  // states a request has. A failure must SAY so: silently doing nothing here
  // would read as "there is no file", which is exactly the lie this codebase
  // keeps having to un-tell.
  //
  // B-03. That request now asks for a PREVIEW (`?disposition=inline`) and puts
  // the file in a new tab instead of the viewer's Downloads folder -- the bytes
  // still come through the authenticated, tenant-scoped service, which is the
  // whole point of B13 and is not negotiable. `previewInNewTab` owns the three
  // things that are easy to get wrong (gesture-window, blob-origin allowlist,
  // revoking the URL); a file it will not render still downloads, and that is
  // reported here rather than looking like nothing happened.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewError, setViewError] = useState('');
  // Not an error: the preview was refused (unsupported type, or a popup
  // blocker) and the file was SAVED instead. The viewer must be told which
  // of the two happened -- but in the voice of a notice, not a failure.
  const [viewNotice, setViewNotice] = useState('');

  const handleView = async (target: CustomerKycDocument) => {
    setViewingId(target.id);
    setViewError('');
    setViewNotice('');
    try {
      const outcome = await previewInNewTab({
        fetchBlob: () => customerKycService.preview(target),
        download: () => customerKycService.download(target),
      });
      setViewNotice(previewOutcomeMessage(outcome));
    } catch {
      setViewError('That document could not be opened. Refresh and try again.');
    } finally {
      setViewingId(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-lg bg-gray-100 p-2 dark:bg-gray-700">
            <DocumentTextIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
              {document.document_type_display}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {document.original_filename || document.name}
              {document.uploaded_by_name ? ` · uploaded by ${document.uploaded_by_name}` : ''}
              {document.created_at ? ` · ${formatDate(document.created_at)}` : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[document.status] ?? 'default'} size="sm">
            {document.status_display}
          </Badge>
          {/* B13 — a plain href would have to point at `/policy/media/`, which
              nginx serves with no session at all. The server no longer emits
              that URL; the bytes come through the authenticated, tenant-scoped
              download route instead. */}
          {document.download_url && (
            <button
              type="button"
              onClick={() => void handleView(document)}
              disabled={viewingId === document.id}
              className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
            >
              {viewingId === document.id ? 'Opening…' : 'View'}
            </button>
          )}
          {canVerify && (
            <>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<CheckCircleIcon className="h-4 w-4" />}
                onClick={() => onVerdict(document, true)}
              >
                Verify
              </Button>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<XCircleIcon className="h-4 w-4" />}
                onClick={() => onVerdict(document, false)}
              >
                Reject
              </Button>
            </>
          )}
          {canDelete && (
            <button
              type="button"
              title="Remove document"
              aria-label={`Remove ${document.document_type_display}`}
              className="text-gray-500 hover:text-red-600 disabled:opacity-50 dark:text-gray-400 dark:hover:text-red-400"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(document.id)}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* The verdict and its remark, shown to whoever is looking — including the
          customer, for whom this is the whole point of the panel. */}
      {document.verification_remarks && (
        <p
          className={`mt-2 text-xs ${
            document.status === 'rejected' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          {document.verification_remarks}
          {document.verified_by_name ? ` — ${document.verified_by_name}` : ''}
          {document.verified_at ? `, ${formatDate(document.verified_at)}` : ''}
        </p>
      )}

      {document.extraction_status === 'unconfirmed' && (
        <ExtractionReview document={document} customerId={customerId} />
      )}
      {document.extraction_status === 'confirmed' && (
        <p className="mt-2 text-xs text-green-700 dark:text-green-400">
          Details confirmed
          {document.extraction_confirmed_by_name ? ` by ${document.extraction_confirmed_by_name}` : ''}
          {document.extraction_confirmed_at ? ` on ${formatDate(document.extraction_confirmed_at)}` : ''}.
        </p>
      )}
      {note && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{note}</p>}
      {viewError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{viewError}</p>}
      {viewNotice && <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">{viewNotice}</p>}
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function KycDocumentsPanel({
  customerId,
  canVerify,
  title = 'KYC Documents',
  subtitle,
}: {
  customerId: string;
  /** Broker staff and the contact's POSP only. The server decides regardless;
   *  this stops the buttons being drawn for someone who cannot use them. */
  canVerify: boolean;
  title?: string;
  subtitle?: string;
}) {
  const { data, isLoading, isError, error, refetch } = useCustomerKycDocuments(customerId);
  const uploadMutation = useUploadCustomerKycDocument(customerId);
  const verdictMutation = useVerifyCustomerKycDocument(customerId);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [documentType, setDocumentType] = useState('pan');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [verdictFor, setVerdictFor] = useState<{ document: CustomerKycDocument; approve: boolean } | null>(
    null
  );

  const documents = data?.results ?? [];

  const submitUpload = () => {
    if (!pendingFile) return;
    uploadMutation.mutate(
      { document_type: documentType, file: pendingFile },
      {
        onSuccess: () => {
          setPendingFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader
        title={title}
        subtitle={
          subtitle ??
          'Upload an identity document — we read the details off it for you to check before they are saved.'
        }
      />

      {/* Upload */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,14rem)_1fr_auto] sm:items-end">
        <Select
          id="kyc-document-type"
          name="kyc-document-type"
          label="Document type"
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          options={CUSTOMER_KYC_DOCUMENT_TYPES.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
        />
        <div>
          <label htmlFor="kyc-document-file" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            File
          </label>
          <input
            id="kyc-document-file"
            name="kyc-document-file"
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-primary-600 file:px-3 file:py-1 file:text-sm file:font-medium file:text-white hover:file:bg-primary-700 dark:border-gray-600 dark:file:bg-primary-500 dark:hover:file:bg-primary-400"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">PDF or a photo (JPG, PNG, WEBP, HEIC), up to 10 MB.</p>
        </div>
        <Button
          variant="primary"
          leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
          disabled={!pendingFile || uploadMutation.isPending}
          onClick={submitUpload}
        >
          {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
        </Button>
      </div>

      {/* Loading, error and empty are THREE states. A failed request rendered as
          "no documents on file" is an authoritative-looking lie on a compliance
          screen — and it is this codebase's most repeated bug. */}
      {isLoading ? (
        <p className="py-4 text-sm text-gray-500 dark:text-gray-400">Loading documents…</p>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:bg-red-900/20 dark:border-red-900">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              We could not load the documents on file — this is not the same as there being none.
            </p>
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {error instanceof Error ? error.message : 'The request failed.'}
            </p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : documents.length === 0 ? (
        <p className="py-4 text-sm text-gray-500 dark:text-gray-400">No KYC documents have been uploaded yet.</p>
      ) : (
        <div className="space-y-3">
          {documents.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              customerId={customerId}
              canVerify={canVerify}
              // A pending document can be withdrawn by whoever is looking; once
              // it has been ruled on, the verdict is a record and only the
              // broker side may remove it (the server enforces the same rule).
              canDelete={canVerify || document.status === 'pending'}
              onVerdict={(target, approve) => setVerdictFor({ document: target, approve })}
            />
          ))}
        </div>
      )}

      <VerdictModal
        document={verdictFor?.document ?? null}
        approve={verdictFor?.approve ?? true}
        isLoading={verdictMutation.isPending}
        onClose={() => setVerdictFor(null)}
        onConfirm={(remarks) => {
          if (!verdictFor) return;
          verdictMutation.mutate(
            { documentId: verdictFor.document.id, remarks, approve: verdictFor.approve },
            { onSuccess: () => setVerdictFor(null) }
          );
        }}
      />
    </Card>
  );
}
